"use client";

import { motion } from "framer-motion";
import React from "react";
import { useCookieConsent } from "../../../insights/src/cookies/cookie-context";
import type { CookieConsentOptions } from "../../../insights/src/cookies/types";
import { Button } from "../button";
import { Icon } from "../icon";
import { HStack, VStack } from "../layout/stack";
import { cn } from "../lib/utils";
import { Switch } from "../switch";

interface CookieBannerProps {
	variant?: "default" | "light";
}

export function CookieBanner({ variant = "default" }: CookieBannerProps) {
	const {
		showBanner,
		acceptAll,
		rejectAll,
		openSettings,
		showSettings,
		closeSettings,
		cookieConsent,
		updatePreferences,
	} = useCookieConsent();

	if (!showBanner && !showSettings) return null;

	return (
		<>
			{showBanner && !showSettings && (
				<motion.div
					initial={{ y: "100vh", opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					transition={{ duration: 1, ease: "easeInOut" }}
					className="fixed inset-x-0 bottom-0 z-50"
				>
					<div className="fixed bottom-4 left-4 z-50 max-w-[360px] rounded-lg bg-white border border-gray-200 text-foreground-secondary p-4 shadow-lg">
						<VStack className="gap-3">
							<div>
								<h2 className="mb-1 text-sm font-medium text-foreground">
									Cookie Preferences
								</h2>
								<p className="text-xs text-foreground-secondary">
									We use cookies to enhance your experience. Choose your
									preferences below. Read about our privacy policy{" "}
									<a
										href="/privacy"
										target="_blank"
										rel="noopener noreferrer"
										className="text-foreground-secondary hover:text-foreground underline"
									>
										here
									</a>
									.
								</p>
							</div>
							<HStack className="gap-min justify-between">
								<Button
									size="sm"
									intent="ghost"
									onClick={openSettings}
									className="cursor-pointer"
								>
									Settings
								</Button>
								<HStack className="gap-2">
									<Button
										size="sm"
										intent="ghost"
										onClick={rejectAll}
										className="cursor-pointer"
									>
										Reject
									</Button>
									<Button
										size="sm"
										intent="subtle"
										onClick={acceptAll}
										className="cursor-pointer"
									>
										Accept
									</Button>
								</HStack>
							</HStack>
						</VStack>
					</div>
				</motion.div>
			)}

			{showSettings && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 p-4">
					<div className="relative w-full max-w-[480px] rounded-lg bg-white border border-gray-200 text-foreground-secondary p-6 shadow-xl">
						<Button
							className="absolute right-4 top-4 hover:bg-gray-100 cursor-pointer text-foreground"
							size="icon"
							intent="ghost"
							onClick={closeSettings}
						>
							<Icon kind="close" />
							<span className="sr-only">Close</span>
						</Button>

						<VStack className="gap-6">
							<div>
								<h2 className="mb-2 text-lg font-medium text-foreground">
									Cookie Settings
								</h2>
							</div>

							<CookieSettingsForm
								initialConsent={cookieConsent}
								onSave={updatePreferences}
								onCancel={closeSettings}
								variant={variant}
							/>
						</VStack>
					</div>
				</div>
			)}
		</>
	);
}

function CookieSettingsForm({
	initialConsent,
	onSave,
	onCancel,
	variant = "default",
}: {
	initialConsent: CookieConsentOptions | null;
	onSave: (preferences: CookieConsentOptions) => void;
	onCancel: () => void;
	variant?: "default" | "light";
}) {
	const [preferences, setPreferences] = React.useState<CookieConsentOptions>({
		necessary: true,
		analytics: initialConsent?.analytics ?? false,
		marketing: initialConsent?.marketing ?? false,
	});

	const handleToggle = (
		category: keyof Omit<CookieConsentOptions, "necessary">,
	) => {
		setPreferences((prev) => ({
			...prev,
			[category]: !prev[category],
		}));
	};

	const handleSave = () => {
		onSave(preferences);
	};

	return (
		<VStack className="gap-6">
			<VStack className="gap-3">
				<div className="rounded-md border border-gray-200 bg-gray-50 p-4">
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-foreground-secondary">
								Necessary Cookies
							</h3>
							<p className="text-xs text-foreground">
								Required for the website to function. Cannot be disabled.
							</p>
						</div>
						<Switch checked disabled variant={variant} />
					</div>
				</div>

				<div
					className={cn(
						"rounded-md border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50",
					)}
				>
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-foreground-secondary">
								Analytics Cookies
							</h3>
							<p className="text-xs text-foreground">
								Help us improve by collecting anonymous usage data.
							</p>
						</div>
						<Switch
							checked={preferences.analytics}
							onClick={() => handleToggle("analytics")}
							variant={variant}
						/>
					</div>
				</div>

				<div
					className={cn(
						"rounded-md border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50",
					)}
				>
					<div className="flex items-center justify-between">
						<div>
							<h3 className="text-sm font-medium text-foreground-secondary">
								Marketing Cookies
							</h3>
							<p className="text-xs text-foreground">
								Used to show you relevant ads across websites.
							</p>
						</div>
						<Switch
							checked={preferences.marketing}
							onClick={() => handleToggle("marketing")}
							variant={variant}
						/>
					</div>
				</div>
			</VStack>

			<HStack className="justify-end gap-min">
				<Button
					intent="ghost"
					size="sm"
					onClick={onCancel}
					className="cursor-pointer text-gray-700"
				>
					Cancel
				</Button>
				<Button
					intent="subtle"
					size="sm"
					onClick={handleSave}
					className="cursor-pointer"
				>
					Save Preferences
				</Button>
			</HStack>
		</VStack>
	);
}
